import { useCallback, useMemo, useState } from 'react'
import { Check, X, Smile, TrendingUp, Copyright, Users, FileCheck2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { DangerDialog } from '@/components/DangerDialog'
import { RegistryCrudTable, type RegField, type RegRow } from '@/components/RegistryCrudTable'
import {
  fetchStickerModeration,
  fetchTrendingStickers,
  fetchCopyrightComplaints,
  fetchStickerAuthors,
  moderateStickerSet,
  CAT_STR as S,
  type CatStickerSetRow,
  type CopyrightComplaintRow,
  type StickerAuthorRow,
} from './catalogApi'

interface TgsRuleRow extends RegRow {
  id: number
  rule_name: string
  param: string
  value: string
  action: string
  enabled: boolean
  updated_at: string
}

const setStatusOptions = [
  { value: 'pending', label: S.pending, status: 'pending' as const },
  { value: 'active', label: S.active, status: 'active' as const },
  { value: 'rejected', label: S.rejected, status: 'rejected' as const },
]

/**
 * A21.02 — Stiker moderatsiyasi. GET /admin/stickers/sets?status=pending; tasdiq/rad
 * → POST /admin/stickers/sets/:id/moderate {approve, reason}. Faqat kutayotgan navbat.
 */
export function StickerModerationScreen() {
  const { toast } = useToast()
  const [rejectTarget, setRejectTarget] = useState<CatStickerSetRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(S.done, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
        throw e
      }
    },
    [toast],
  )

  const columns = useMemo<AdminColumn<CatStickerSetRow>[]>(
    () => [
      {
        key: 'title',
        header: S.title,
        type: 'text',
        minWidth: 220,
        render: (r) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-primary">{r.title || '—'}</span>
            <span className="block truncate text-xs text-secondary">
              {r.short_name ? `/${r.short_name}` : ''}
              {r.owner_name ? ` · ${r.owner_name}` : ` · #${r.owner_id}`}
            </span>
          </div>
        ),
      },
      { key: 'status', header: S.status, type: 'enum', width: 150, filterable: true, options: setStatusOptions },
      { key: 'sticker_count', header: S.count, type: 'number', width: 100, align: 'right' },
      { key: 'installs', header: S.installs, type: 'number', width: 110, align: 'right' },
      { key: 'animated', header: S.animated, type: 'bool', width: 110 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<CatStickerSetRow>[]>(
    () => [
      {
        id: 'approve',
        label: S.approve,
        icon: <Check size={13} />,
        visible: (r) => r.status !== 'active',
        onSelect: (r) => void run(() => moderateStickerSet(r.id, true, '')),
      },
      {
        id: 'reject',
        label: S.reject,
        icon: <X size={13} />,
        danger: true,
        visible: (r) => r.status !== 'rejected',
        onSelect: (r) => setRejectTarget(r),
      },
    ],
    [run],
  )

  return (
    <>
      <PageHeader title={S.stickerModeration} screenId="A21.02" />
      <DataTable<CatStickerSetRow>
        key={reloadKey}
        screenId="A21.02"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchStickerModeration}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <Smile size={22} />, title: S.emptyQueue }}
      />
      {rejectTarget && (
        <DangerDialog
          open
          onClose={() => setRejectTarget(null)}
          screenId="A21.02"
          level={2}
          title={`${S.reject} — ${rejectTarget.title}`}
          description={S.rejectPrompt}
          confirmLabel={S.reject}
          onConfirm={async ({ reason }) => {
            await run(() => moderateStickerSet(rejectTarget.id, false, reason))
            setRejectTarget(null)
          }}
        />
      )}
    </>
  )
}

/** A21.03 — Trending stikerlar. Active to'plamlar, o'rnatishlar bo'yicha (eng ko'p — birinchi). */
export function TrendingStickersScreen() {
  const columns = useMemo<AdminColumn<CatStickerSetRow>[]>(
    () => [
      {
        key: 'title',
        header: S.title,
        type: 'text',
        minWidth: 220,
        render: (r) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-primary">{r.title || '—'}</span>
            <span className="block truncate text-xs text-secondary">
              {r.short_name ? `/${r.short_name}` : ''}
              {r.owner_name ? ` · ${r.owner_name}` : ` · #${r.owner_id}`}
            </span>
          </div>
        ),
      },
      { key: 'installs', header: S.installs, type: 'number', width: 120, align: 'right' },
      { key: 'sticker_count', header: S.count, type: 'number', width: 100, align: 'right' },
      { key: 'animated', header: S.animated, type: 'bool', width: 110 },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.trendingStickers} screenId="A21.03" />
      <DataTable<CatStickerSetRow>
        screenId="A21.03"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchTrendingStickers}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <TrendingUp size={22} /> }}
      />
    </>
  )
}

/** A21.04 — Mualliflik shikoyatlari (DMCA/copyright). Greenfield → fail-soft bo'sh. */
export function CopyrightComplaintsScreen() {
  const columns = useMemo<AdminColumn<CopyrightComplaintRow>[]>(
    () => [
      { key: 'set_title', header: S.setTitle, type: 'text', minWidth: 200 },
      { key: 'complainant', header: S.complainant, type: 'text', width: 180 },
      { key: 'reason', header: S.reason, type: 'longtext', minWidth: 220 },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 140,
        filterable: true,
        options: [
          { value: 'open', label: S.pending, status: 'open' },
          { value: 'resolved', label: S.done, status: 'approved' },
          { value: 'rejected', label: S.rejected, status: 'rejected' },
        ],
      },
      { key: 'created_at', header: S.created, type: 'date', width: 150 },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.copyright} screenId="A21.04" />
      <DataTable<CopyrightComplaintRow>
        screenId="A21.04"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchCopyrightComplaints}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Copyright size={22} /> }}
      />
    </>
  )
}

/** A21.05 — Stiker mualliflari. Greenfield → fail-soft bo'sh. */
export function StickerAuthorsScreen() {
  const columns = useMemo<AdminColumn<StickerAuthorRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text', minWidth: 200 },
      { key: 'sets_count', header: S.setsCount, type: 'number', width: 120, align: 'right' },
      { key: 'installs_total', header: S.installsTotal, type: 'number', width: 150, align: 'right' },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 140,
        filterable: true,
        options: [
          { value: 'active', label: S.active, status: 'active' },
          { value: 'blocked', label: S.blocked, status: 'blocked' },
        ],
      },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.authors} screenId="A21.05" />
      <DataTable<StickerAuthorRow>
        screenId="A21.05"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchStickerAuthors}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Users size={22} /> }}
      />
    </>
  )
}

/**
 * A21.06 — Animatsion stikerlar (TGS) validatsiyasi. Endi JONLI: admin
 * TGS-lint qoidalarini (o'lcham/davomiylik/format cheklovlari + violation
 * harakati) o'zi boshqaradi — real CRUD `/admin/stickers/tgs-rules`
 * (`sticker_tgs_rules`, migration 000249). Soxta ma'lumot yo'q.
 */
export function TgsValidationScreen() {
  const columns = useMemo<AdminColumn<TgsRuleRow>[]>(
    () => [
      { key: 'rule_name', header: S.tgsRuleName, type: 'text', minWidth: 200 },
      { key: 'param', header: S.tgsParam, type: 'text', width: 160 },
      { key: 'value', header: S.tgsValue, type: 'text', width: 140 },
      {
        key: 'action',
        header: S.tgsAction,
        type: 'enum',
        width: 130,
        filterable: true,
        options: [
          { value: 'reject', label: 'reject', status: 'rejected' },
          { value: 'warn', label: 'warn', status: 'pending' },
          { value: 'allow', label: 'allow', status: 'approved' },
        ],
      },
      { key: 'enabled', header: S.enabled, type: 'bool', width: 100, align: 'center' },
      { key: 'updated_at', header: S.checkedAt, type: 'date', width: 150 },
    ],
    [],
  )
  const fields = useMemo<RegField[]>(
    () => [
      { name: 'rule_name', placeholder: S.tgsRuleName },
      { name: 'param', placeholder: S.tgsParam, mono: true },
      { name: 'value', placeholder: S.tgsValue, mono: true },
      {
        name: 'action',
        placeholder: S.tgsAction,
        type: 'select',
        options: [
          { value: 'reject', label: 'reject' },
          { value: 'warn', label: 'warn' },
          { value: 'allow', label: 'allow' },
        ],
      },
      { name: 'enabled', placeholder: S.enabled, type: 'checkbox', defaultValue: 'true' },
    ],
    [],
  )
  return (
    <RegistryCrudTable<TgsRuleRow>
      screenId="A21.06"
      title={S.tgs}
      description={S.tgsDesc}
      addLabel={S.addTgsRule}
      path="/admin/stickers/tgs-rules"
      listKey="rules"
      searchKeys={['rule_name', 'param', 'action']}
      fields={fields}
      columns={columns}
      icon={<FileCheck2 size={22} />}
    />
  )
}
